import React, { useEffect, useState } from "react";
import { Card, CardContent, IconButton, Chip } from "@material-ui/core";
import { Link } from "react-router-dom";
import { People, Search } from "@material-ui/icons";
import SimDataGrid from "../../shared/data-grid/SimDataGrid";
import getStatusName from "../../shared/functions/getStatusName";
import { useCompany } from "../../../contexts/CompanyContext";

/**
 * DealerDownstreamTable component renders a table displaying downstream dealers.
 *
 * Props:
 * - dealers: An array of dealer objects, each containing details like id, contact name, contact surname, phone, and rank.
 *
 * State:
 * - rows: An array of formatted dealer objects for the table display.
 *
 * Effects:
 * - Updates rows state when the dealers prop changes.
 *
 * UI Elements:
 * - A data grid inside a card component displaying dealer information with columns for Name, Phone, Level, and Action.
 * - Each dealer row includes action buttons linking to detailed views or downstream views.
 * @param {object[]} dealers - An array of dealer objects.
 */
export default function DealerDownstreamTable({ dealers }) {
  const [rows, setRows] = useState([]);
  const { lifecycleStatuses } = useCompany();

  useEffect(() => {
    setRows(
      dealers &&
        dealers.map(d => ({
          id: d.id,
          name: `Z-${d.id}: ${d.first_name} ${d.surname}`,
          phone: `0${d.phone.slice(3)}`,
          is_leader: d.is_leader,
          email_verified: d.email_verified,
          phone_verified: d.phone_verified,
          status: d.refs_user_lifecycle_status_id,
        }))
    );
  }, [dealers]);

  const columns = [
    { field: "name", headerName: "Name", width: 200 },
    { field: "phone", headerName: "Phone", width: 120 },
    {
      field: "email_verified",
      headerName: "Email Verified",
      sortable: true,
      width: 140,
      // eslint-disable-next-line no-confusing-arrow
      renderCell: params => {
        return (
          <div
            style={{
              textTransform: "capitalize",
              justifyContent: "center",
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Chip
              color={params.row.email_verified ? "primary" : "secondary"}
              label={params.row.email_verified ? "Yes" : "No"}
              size="small"
            />
          </div>
        );
      },
    },
    {
      field: "phone_verified",
      headerName: "Phone Verified",
      sortable: true,
      width: 140,
      // eslint-disable-next-line no-confusing-arrow
      renderCell: params => {
        return (
          <div
            style={{
              textTransform: "capitalize",
              justifyContent: "center",
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Chip
              color={params.row.phone_verified ? "primary" : "secondary"}
              label={params.row.phone_verified ? "Yes" : "No"}
              size="small"
            />
          </div>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      sortable: true,
      width: 140,
      renderCell: params => {
        return (
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ textTransform: "capitalize" }}
          >
            <Chip
              color={params.row.status === 2 ? "primary" : "secondary"}
              label={getStatusName(
                parseInt(params.row.status),
                lifecycleStatuses
              )}
              size="small"
            />
          </div>
        );
      },
    },
    {
      field: "action",
      headerName: "Detail / Downstream",
      width: 200,
      renderCell: params => (
        <div
          className="d-flex justify-content-between align-items-center"
          style={{ cursor: "pointer" }}
        >
          <Link to={`/user-detail/${params.row.id}`}>
            <IconButton>
              <Search />
            </IconButton>
          </Link>
          {params.row.is_leader && (
            <Link to={`/child-dealer-downstream/${params.row.id}`}>
              <IconButton>
                <People />
              </IconButton>
            </Link>
          )}
        </div>
      ),
    },
  ];

  return (
    <Card>
      <CardContent>
        <SimDataGrid data={rows} columns={columns} />
      </CardContent>
    </Card>
  );
}
